import { Capacitor } from '@capacitor/core';
import { PLAYER_STATUS } from 'constants/gameConstants';

const LEGACY_SESSION_PREFIX = 'hand-history-session-start';
const LEGACY_SESSION_ENDED_PREFIX = 'hand-history-session-ended';
const LEGACY_SESSION_DATA_PREFIX = 'hand-history-session-data';

type HandHistoryPageSession = {
  startedAt?: number;
  ended: boolean;
  // EN-10944: explicit exits can resume within 30 minutes; kicks end immediately.
  exitedAt?: number;
  lastSeenAt?: number;
  frozenThroughGameNo?: number;
  firstGameNo?: number;
  // Do not advance this for hands merely observed while waiting or sitting out.
  lastParticipatedGameNo?: number;
  excludedThroughGameNo?: number;
  completedThroughGameNo?: number;
  excludedGameNos?: number[];
  data?: unknown;
  panelOpen?: boolean;
};

// Boundary metadata only. The server re-grants played hands after restoration;
// cards, keys and snapshots never go into either storage. Web stays per tab;
// the native shell uses durable storage to survive a killed WebView.
const pageSessions = new Map<string, HandHistoryPageSession>();
const PAGE_SESSION_STORAGE_PREFIX = 'hand-history-page-session:';
export const HAND_HISTORY_RETURN_WINDOW_MS = 30 * 60 * 1000;
// How often the native shell refreshes lastSeenAt (useHandHistoryNativeCheckpoint).
export const HAND_HISTORY_CHECKPOINT_INTERVAL_MS = 15000;

const SESSION_END_STATUSES = new Set<number>([
  PLAYER_STATUS.IMMEDIATE_KICK,
  PLAYER_STATUS.KICK_AFTER_CHARGE_TIMER_EXPIRY
]);
const SESSION_EXIT_STATUSES = new Set<number>([
  PLAYER_STATUS.EXIT_TABLE,
  PLAYER_STATUS.EXIT_TABLE_WAIT_FOR_BB,
  PLAYER_STATUS.EXIT_TABLE_POST_BLIND,
  PLAYER_STATUS.SHOW_CARDS_FOR_EXIT
]);

export const getHandHistorySessionKey = (tableId: string, playerGUID: string) =>
  `${tableId}:${playerGUID}`;

const tabStorage = (): Storage | undefined => {
  try {
    if (typeof window === 'undefined') return undefined;
    return Capacitor.isNativePlatform() ? window.localStorage : window.sessionStorage;
  } catch {
    return undefined; // Storage can be blocked (privacy settings, sandboxed frames).
  }
};

const positiveInteger = (value: unknown) =>
  Number.isSafeInteger(value) && (value as number) > 0 ? (value as number) : undefined;

const readStoredPageSession = (key: string): HandHistoryPageSession | undefined => {
  try {
    const raw = tabStorage()?.getItem(PAGE_SESSION_STORAGE_PREFIX + key);
    if (!raw) return undefined;
    const stored = JSON.parse(raw);
    if (!stored || typeof stored !== 'object') return undefined;
    return {
      ended: stored.ended === true,
      exitedAt: positiveInteger(stored.exitedAt),
      lastSeenAt: positiveInteger(stored.lastSeenAt),
      frozenThroughGameNo: positiveInteger(stored.frozenThroughGameNo),
      startedAt: positiveInteger(stored.startedAt),
      firstGameNo: positiveInteger(stored.firstGameNo),
      lastParticipatedGameNo: positiveInteger(stored.lastParticipatedGameNo),
      excludedThroughGameNo: positiveInteger(stored.excludedThroughGameNo),
      completedThroughGameNo: positiveInteger(stored.completedThroughGameNo),
      excludedGameNos: Array.isArray(stored.excludedGameNos)
        ? stored.excludedGameNos
            .filter((n: unknown) => positiveInteger(n) !== undefined)
            .slice(-200)
        : undefined,
      panelOpen: typeof stored.panelOpen === 'boolean' ? stored.panelOpen : undefined
    };
  } catch {
    return undefined; // Unreadable or malformed: start a fresh session.
  }
};

// An allowlist, so hand data can never reach storage. A new boundary field
// belongs here and in readStoredPageSession.
const writeStoredPageSession = (key: string, session: HandHistoryPageSession) => {
  const serialized = JSON.stringify({
    ended: session.ended,
    exitedAt: session.exitedAt,
    lastSeenAt: session.lastSeenAt,
    frozenThroughGameNo: session.frozenThroughGameNo,
    startedAt: session.startedAt,
    firstGameNo: session.firstGameNo,
    lastParticipatedGameNo: session.lastParticipatedGameNo,
    excludedThroughGameNo: session.excludedThroughGameNo,
    completedThroughGameNo: session.completedThroughGameNo,
    excludedGameNos: session.excludedGameNos,
    panelOpen: session.panelOpen
  });
  try {
    const storage = tabStorage();
    // The provider re-persists on every table update; write only on change.
    if (!storage || storage.getItem(PAGE_SESSION_STORAGE_PREFIX + key) === serialized) return;
    storage.setItem(PAGE_SESSION_STORAGE_PREFIX + key, serialized);
  } catch {
    /* Quota or blocked storage: the in-memory session still serves this page. */
  }
};

const storedSessionKeys = (): string[] => {
  try {
    const storage = tabStorage();
    if (!storage) return [];
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key?.startsWith(PAGE_SESSION_STORAGE_PREFIX)) {
        keys.push(key.slice(PAGE_SESSION_STORAGE_PREFIX.length));
      }
    }
    return keys;
  } catch {
    return [];
  }
};

const setPageSession = (key: string, session: HandHistoryPageSession) => {
  pageSessions.set(key, session);
  writeStoredPageSession(key, session);
};

const getPageSession = (tableId: string, playerGUID: string) => {
  const key = getHandHistorySessionKey(tableId, playerGUID);
  const inMemory = pageSessions.get(key);
  const session = inMemory ?? readStoredPageSession(key);
  const awaySince =
    session?.exitedAt ??
    (!inMemory && Capacitor.isNativePlatform() ? session?.lastSeenAt : undefined);
  if (
    session &&
    !session.ended &&
    awaySince !== undefined &&
    Date.now() - awaySince > HAND_HISTORY_RETURN_WINDOW_MS
  ) {
    const ended = endedSession(session);
    setPageSession(key, ended);
    return ended;
  }
  if (session) pageSessions.set(key, session);
  return session;
};

export const readHandHistoryPanelOpen = (tableId: string, playerGUID: string) =>
  getPageSession(tableId, playerGUID)?.panelOpen;

export const persistHandHistoryPanelOpen = (
  tableId: string,
  playerGUID: string,
  panelOpen: boolean
) => {
  if (!tableId || !playerGUID) return;
  const current = getPageSession(tableId, playerGUID);
  setPageSession(getHandHistorySessionKey(tableId, playerGUID), {
    ...current,
    ended: current?.ended ?? false,
    panelOpen
  });
};

export const readHandHistoryFirstGameNo = (tableId: string, playerGUID: string) =>
  getPageSession(tableId, playerGUID)?.firstGameNo;

export const qualifyHandHistorySession = (tableId: string, playerGUID: string, gameNo: number) => {
  const current = getPageSession(tableId, playerGUID);
  if (
    !current ||
    current.ended ||
    !Number.isSafeInteger(gameNo) ||
    gameNo <= 0 ||
    gameNo <= (current.excludedThroughGameNo ?? 0)
  )
    return;
  if (current.firstGameNo !== undefined) return;
  setPageSession(getHandHistorySessionKey(tableId, playerGUID), {
    ...current,
    firstGameNo: gameNo
  });
};

export const readHandHistorySessionStartedAt = (tableId: string, playerGUID: string) =>
  getPageSession(tableId, playerGUID)?.startedAt;

export const hasEndedHandHistorySession = (tableId: string, playerGUID: string) =>
  getPageSession(tableId, playerGUID)?.ended === true;

export const resolveHandHistorySessionStartedAt = ({
  now,
  storedSessionStartedAt,
  sessionEnded = false
}: {
  now: number;
  storedSessionStartedAt?: number;
  sessionEnded?: boolean;
}) => {
  if (sessionEnded) return now;
  return storedSessionStartedAt && storedSessionStartedAt > 0 ? storedSessionStartedAt : now;
};

export const isHandHistorySessionFreezeStatus = (status: unknown, tableType?: unknown) => {
  if (Number(status) !== PLAYER_STATUS.BUSTED) return false;

  // A cash player can briefly enter BUSTED while the rebuy flow restores their
  // stack. Keep that page session alive so the concluding hand can reach CDN.
  return tableType === undefined || String(tableType).toLowerCase() !== 's';
};

export const isHandHistorySessionEndStatus = (status: unknown) => {
  const numericStatus = Number(status);
  return Number.isFinite(numericStatus) && SESSION_END_STATUSES.has(numericStatus);
};

export const isHandHistorySessionExitStatus = (status: unknown) =>
  SESSION_EXIT_STATUSES.has(Number(status));

export const readHandHistoryFrozenThroughGameNo = (tableId: string, playerGUID: string) =>
  getPageSession(tableId, playerGUID)?.frozenThroughGameNo;

export const readHandHistoryExcludedGameNos = (tableId: string, playerGUID: string) =>
  getPageSession(tableId, playerGUID)?.excludedGameNos ?? [];

export const freezeHandHistorySession = (tableId: string, playerGUID: string, gameNo: number) => {
  const current = getPageSession(tableId, playerGUID);
  if (!current || current.ended || !positiveInteger(gameNo)) return;
  setPageSession(getHandHistorySessionKey(tableId, playerGUID), {
    ...current,
    frozenThroughGameNo: current.frozenThroughGameNo ?? gameNo
  });
};

/**
 * An explicit exit is resumable. Repeated exit callbacks must not extend it.
 * `excludeRunningHand: false` is for a leave that could not be sent: the
 * player is still seated in that hand, so it remains theirs.
 */
export const pauseHandHistorySession = (
  tableId: string,
  playerGUID: string,
  { excludeRunningHand = true }: { excludeRunningHand?: boolean } = {}
) => {
  const current = getPageSession(tableId, playerGUID);
  if (!current || current.ended || !current.startedAt) return;
  const running = current.lastParticipatedGameNo;
  const excluded = new Set(current.excludedGameNos);
  // Keep prior completed hands, but an explicit exit still excludes the hand
  // in progress (EN-9775 item 35). Disconnect/native suspension does not.
  if (excludeRunningHand && running && running > (current.completedThroughGameNo ?? 0))
    excluded.add(running);
  setPageSession(getHandHistorySessionKey(tableId, playerGUID), {
    ...current,
    // Hands are refetched on return; do not hold them for a table left behind.
    data: undefined,
    exitedAt: current.exitedAt ?? Date.now(),
    excludedGameNos: Array.from(excluded)
      .sort((a, b) => a - b)
      .slice(-200)
  });
};

/** Periodic/background checkpoint: survives native kill without storing hand data. */
export const checkpointHandHistorySession = (tableId: string, playerGUID: string) => {
  const current = getPageSession(tableId, playerGUID);
  if (!current || current.ended || current.exitedAt) return;
  setPageSession(getHandHistorySessionKey(tableId, playerGUID), {
    ...current,
    lastSeenAt: Date.now()
  });
};

// The provider persists on table updates; restamping every frame would rewrite
// durable storage each time. The native checkpoint keeps it current between.
const freshLastSeenAt = (lastSeenAt?: number) => {
  const now = Date.now();
  return lastSeenAt &&
    now - lastSeenAt >= 0 &&
    now - lastSeenAt < HAND_HISTORY_CHECKPOINT_INTERVAL_MS
    ? lastSeenAt
    : now;
};

export const persistHandHistorySession = (
  tableId: string,
  playerGUID: string,
  startedAt: number,
  participatedGameNo?: number
) => {
  if (!tableId || !playerGUID) return;

  const current = getPageSession(tableId, playerGUID);
  const participated = positiveInteger(participatedGameNo);
  setPageSession(getHandHistorySessionKey(tableId, playerGUID), {
    ...current,
    startedAt,
    exitedAt: undefined,
    ...(Capacitor.isNativePlatform() ? { lastSeenAt: freshLastSeenAt(current?.lastSeenAt) } : {}),
    // Keep the highest: an unversioned stale frame can arrive out of order, and
    // a regressed value would let an exit exclude too little.
    lastParticipatedGameNo: participated
      ? Math.max(current?.lastParticipatedGameNo ?? 0, participated)
      : current?.lastParticipatedGameNo,
    ended: false
  });
};

export const readHandHistorySessionData = <T>(tableId: string, playerGUID: string): T | undefined =>
  getPageSession(tableId, playerGUID)?.data as T | undefined;

export const persistHandHistorySessionData = (
  tableId: string,
  playerGUID: string,
  data: unknown
) => {
  if (!tableId || !playerGUID) return;

  const current = getPageSession(tableId, playerGUID);
  if (current?.ended || current?.exitedAt) return;
  const completedThrough = positiveInteger(
    (data as { completedThroughGameNo?: number } | undefined)?.completedThroughGameNo
  );
  setPageSession(getHandHistorySessionKey(tableId, playerGUID), {
    ...current,
    ended: current?.ended ?? false,
    completedThroughGameNo: completedThrough
      ? Math.max(current?.completedThroughGameNo ?? 0, completedThrough)
      : current?.completedThroughGameNo,
    data
  });
};

export const clearHandHistorySessionData = (tableId: string, playerGUID: string) => {
  if (!tableId || !playerGUID) return;

  const current = getPageSession(tableId, playerGUID);
  if (!current) return;
  setPageSession(getHandHistorySessionKey(tableId, playerGUID), {
    ...current,
    data: undefined
  });
};

export const endHandHistorySession = (tableId: string, playerGUID: string) => {
  if (!tableId || !playerGUID) return;
  const current = getPageSession(tableId, playerGUID);
  setPageSession(getHandHistorySessionKey(tableId, playerGUID), endedSession(current));
};

const endedSession = (current?: HandHistoryPageSession): HandHistoryPageSession => ({
  ended: true,
  excludedThroughGameNo: Math.max(
    current?.excludedThroughGameNo ?? 0,
    current?.lastParticipatedGameNo ?? 0
  )
});

/** Force an immediate end for every local player boundary on this table. */
export const endTableHandHistorySessions = (tableId: string) => {
  // Include sessions this page has not read since a refresh.
  const keys = new Set(
    [...Array.from(pageSessions.keys()), ...storedSessionKeys()].filter((key) =>
      key.startsWith(`${tableId}:`)
    )
  );
  keys.forEach((key) => {
    const session = pageSessions.get(key) ?? readStoredPageSession(key);
    setPageSession(key, endedSession(session));
  });
};

/** Explicit exit can unmount the provider before a leaving status is broadcast. */
export const pauseTableHandHistorySessions = (tableId: string) => {
  const keys = new Set([...Array.from(pageSessions.keys()), ...storedSessionKeys()]);
  keys.forEach((key) => {
    if (key.startsWith(`${tableId}:`))
      pauseHandHistorySession(tableId, key.slice(tableId.length + 1));
  });
};

export const clearLegacyHandHistoryStorage = (
  storage: Pick<Storage, 'removeItem'>,
  tableId: string,
  playerGUID: string
) => {
  if (!tableId || !playerGUID) return;

  [LEGACY_SESSION_PREFIX, LEGACY_SESSION_ENDED_PREFIX, LEGACY_SESSION_DATA_PREFIX].forEach(
    (prefix) => {
      try {
        storage.removeItem(`${prefix}:${tableId}:${playerGUID}`);
      } catch {
        /* Browser storage can be unavailable in restricted browser contexts. */
      }
    }
  );
};

export const resetHandHistoryPageSessionsForTests = () => {
  pageSessions.clear();
  storedSessionKeys().forEach((key) => tabStorage()?.removeItem(PAGE_SESSION_STORAGE_PREFIX + key));
};

/** A refresh: the module's memory is gone, the tab's sessionStorage is not. */
export const simulateHandHistoryPageReloadForTests = () => {
  pageSessions.clear();
};
